import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  BlockHeroCompact,
  projectPageShellClass,
  projectPageBottomClass,
  blockTechnicalLabelClass,
  blockItalicSecondaryNoteClass,
} from "../../designSystem/projectBlocks";
import ENTRIES, { getEntryBySlug } from "./entries";

function formatDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * /Writing/:slug — single entry page.
 *
 * The shell + hero are provided here so each entry file only renders its
 * own body. Prev/next links to neighbouring entries sit at the bottom,
 * in the same mono caps register as figure captions.
 */
export default function WritingEntry() {
  const { slug } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const entry = getEntryBySlug(slug);

  if (!entry) {
    return (
      <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
        <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
          <BlockHeroCompact
            title="Entry not found"
            description="That entry may have moved or never existed. Head back to the writing index to browse what is here."
            meta={["404"]}
          />
          <Link
            to="/Writing"
            data-cursor="pointer"
            className={`${blockTechnicalLabelClass} inline-block mt-4 hover:text-black dark:hover:text-white transition-colors`}
          >
            ‹ Back to writing
          </Link>
        </div>
      </main>
    );
  }

  const { meta, Component } = entry;

  // Adjacent entries in the ENTRIES array (already sorted newest first).
  const idx = ENTRIES.findIndex((e) => e.meta.slug === slug);
  const newer = idx > 0 ? ENTRIES[idx - 1] : null;
  const older = idx >= 0 && idx < ENTRIES.length - 1 ? ENTRIES[idx + 1] : null;

  const heroMeta = [formatDate(meta.date), ...(meta.tags || [])];

  return (
    <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
      <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
        <BlockHeroCompact
          title={meta.title}
          description={meta.summary}
          meta={heroMeta}
        />

        {/* Entry body */}
        <Component />

        {/* Footer nav — prev / next within writing, plus back to index */}
        <nav className="mt-16 lg:mt-20 pt-6 border-t border-border dark:border-neutral-800 flex flex-wrap items-center justify-between gap-4">
          <Link
            to="/Writing"
            data-cursor="pointer"
            className={`${blockTechnicalLabelClass} hover:text-black dark:hover:text-white transition-colors`}
          >
            ‹ All writing
          </Link>

          <div className="flex items-center gap-6">
            {older ? (
              <Link
                to={`/Writing/${older.meta.slug}`}
                data-cursor="pointer"
                title={`Older · ${older.meta.title}`}
                className={`${blockTechnicalLabelClass} hover:text-black dark:hover:text-white transition-colors`}
              >
                ‹ Older
              </Link>
            ) : (
              <span className={`${blockTechnicalLabelClass} opacity-40`}>‹ Older</span>
            )}
            {newer ? (
              <Link
                to={`/Writing/${newer.meta.slug}`}
                data-cursor="pointer"
                title={`Newer · ${newer.meta.title}`}
                className={`${blockTechnicalLabelClass} hover:text-black dark:hover:text-white transition-colors`}
              >
                Newer ›
              </Link>
            ) : (
              <span className={`${blockTechnicalLabelClass} opacity-40`}>Newer ›</span>
            )}
          </div>
        </nav>

        {!Array.isArray(ENTRIES) || ENTRIES.length === 1 ? (
          <p className={`${blockItalicSecondaryNoteClass} mt-4`}>
            This is the only entry so far.
          </p>
        ) : null}
      </div>
    </main>
  );
}
